import type { ReactNode } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Box } from "../../components/ui/box";
import type { Omittable } from "../../lib/props";
import { CHART_RAMP_TOKENS, useChartColors, type ChartColorToken } from "./colors";
import {
  AXIS_TICK,
  HOVER_CURSOR_FILL,
  HOVER_CURSOR_TRAVEL_CLASS,
  TOOLTIP_MOTION,
  TooltipShell,
  CHART_MOTION,
  useChartMotion,
} from "./internal/recharts";

/** One value series per category. */
export type CategoricalBarChartSeries = Omittable<{
  /** Row field holding this series' value. */
  key: string;
  /** Semantic color; series without one take the ramp in declaration order. */
  colorToken?: ChartColorToken;
}>;

export type CategoricalBarChartProps<D extends object> = Omittable<{
  /** One row per category. */
  data: readonly D[];
  /** Row field holding the category label. */
  categoryKey: string;
  /** At least one value series; several stack or group per `mode`. */
  series: readonly CategoricalBarChartSeries[];
  /** How multiple series share a category. Defaults to `grouped`. */
  mode?: "grouped" | "stacked";
  /** Bar direction: `columns` (default) or `rows` for long category labels. */
  orientation?: "columns" | "rows";
  /** Per-category colors for a single-series chart, index-aligned with `data`
   * (`useCategoricalColors(...).tokens`) so color follows the entity. */
  categoryColorTokens?: readonly ChartColorToken[];
  showGrid?: boolean;
  /** The VALUE axis and its ticks — a reading aid, safe to drop on a small tile. */
  showAxes?: boolean;
  /**
   * The CATEGORY axis. Defaults to `showAxes`, and that default is the one to
   * override rather than accept: the category axis is what NAMES each mark, so
   * hiding it leaves anonymous bars rather than a compact chart. Drop it only
   * for a deliberate micro form whose categories are identified somewhere else.
   */
  showCategoryAxis?: boolean;
  /** Angle crowded column labels (-35°, end-anchored). */
  angledCategoryTicks?: boolean;
  categoryTickFormatter?: (value: string) => string;
  valueTickFormatter?: (value: number) => string;
  /** Label gutter width for `rows` orientation. Defaults to 96. */
  categoryAxisWidth?: number;
  /**
   * Fires for the category under the pointer. The hit target is the whole
   * category BAND the hover cursor paints, not the painted rect: a short bar is
   * a few pixels wide and would otherwise be nearly unclickable.
   */
  onBarClick?: (row: D) => void;
  /** Hover card content for the hovered category; omit for no hover card. */
  renderTooltip?: (row: D) => ReactNode;
  ariaLabel?: string;
}>;

/**
 * The finished categorical bar chart (recharts engine): columns or rows,
 * one series or several (stacked/grouped), entity-owned per-category color for
 * the single-series case. Colors are semantic tokens painted as live `var()`
 * strings; recharts never crosses this surface.
 */
export function CategoricalBarChart<D extends object>({
  data,
  categoryKey,
  series,
  mode = "grouped",
  orientation = "columns",
  categoryColorTokens,
  showGrid,
  showAxes,
  showCategoryAxis = showAxes,
  angledCategoryTicks,
  categoryTickFormatter,
  valueTickFormatter,
  categoryAxisWidth = 96,
  onBarClick,
  renderTooltip,
  ariaLabel,
}: CategoricalBarChartProps<D>) {
  const c = useChartColors();
  const animate = useChartMotion();
  const rows = orientation === "rows";
  const stacked = mode === "stacked" && series.length > 1;
  const perCategory = series.length === 1 ? categoryColorTokens : undefined;

  const categoryAxis = rows ? (
    <YAxis
      dataKey={categoryKey}
      type="category"
      hide={!showCategoryAxis}
      width={categoryAxisWidth}
      tick={AXIS_TICK}
      tickLine={false}
      axisLine={false}
      interval={0}
      {...(categoryTickFormatter ? { tickFormatter: categoryTickFormatter } : null)}
    />
  ) : (
    <XAxis
      dataKey={categoryKey}
      type="category"
      hide={!showCategoryAxis}
      tick={AXIS_TICK}
      tickLine={false}
      axisLine={false}
      tickMargin={8}
      interval={0}
      {...(categoryTickFormatter ? { tickFormatter: categoryTickFormatter } : null)}
      {...(angledCategoryTicks
        ? { angle: -35, textAnchor: "end" as const, height: 52 }
        : null)}
    />
  );
  const valueAxis = rows ? (
    <XAxis
      type="number"
      hide={!showAxes}
      tick={AXIS_TICK}
      tickLine={false}
      axisLine={false}
      tickCount={4}
      allowDecimals={false}
      {...(valueTickFormatter ? { tickFormatter: valueTickFormatter } : null)}
    />
  ) : (
    <YAxis
      type="number"
      hide={!showAxes}
      width={36}
      tick={AXIS_TICK}
      tickLine={false}
      axisLine={false}
      tickCount={4}
      allowDecimals={false}
      {...(valueTickFormatter ? { tickFormatter: valueTickFormatter } : null)}
    />
  );

  // A subtle value-end rounding; stacked segments stay square so interior
  // seams read as one bar.
  const radius: [number, number, number, number] | undefined = stacked
    ? undefined
    : rows
      ? [0, 3, 3, 0]
      : [3, 3, 0, 0];

  return (
    <Box
      width="100%"
      height="100%"
      role={ariaLabel === undefined ? undefined : "img"}
      aria-label={ariaLabel}
    >
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={data as D[]}
          layout={rows ? "vertical" : "horizontal"}
          margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
          {...(onBarClick
            ? {
                cursor: "pointer",
                onClick: (next: { activeTooltipIndex?: number | string | null | undefined }) => {
                  // The active category IS the band the hover cursor paints, so
                  // reading it here makes that whole band the hit target — a
                  // press anywhere in the row, gutter included, answers.
                  // recharts reports the index as a string, and `null` when it
                  // has no category to name; that has to be caught before
                  // coercion, because `Number(null)` is 0 and would answer with
                  // the first row.
                  const active = next.activeTooltipIndex;
                  const row = active == null ? undefined : data[Number(active)];
                  if (row !== undefined) onBarClick(row);
                },
              }
            : null)}
        >
          {showGrid ? (
            <CartesianGrid
              horizontal={!rows}
              vertical={rows}
              stroke={c.grid}
            />
          ) : null}
          {categoryAxis}
          {valueAxis}
          {renderTooltip ? (
            <Tooltip
              isAnimationActive={animate}
              {...TOOLTIP_MOTION}
              cursor={{ fill: HOVER_CURSOR_FILL, className: HOVER_CURSOR_TRAVEL_CLASS }}
              content={({ active, payload }) => {
                const row = payload?.[0]?.payload as D | undefined;
                if (!active || row === undefined) return null;
                return <TooltipShell>{renderTooltip(row)}</TooltipShell>;
              }}
            />
          ) : null}
          {series.map((s, i) => (
            <Bar
              key={s.key}
              dataKey={s.key}
              fill={c.paint(
                s.colorToken ?? CHART_RAMP_TOKENS[i % CHART_RAMP_TOKENS.length],
              )}
              maxBarSize={48}
              isAnimationActive={animate}
              {...CHART_MOTION}
              {...(stacked ? { stackId: "stack" } : null)}
              {...(radius ? { radius } : null)}
            >
              {perCategory
                ? // Cells are index-aligned with `data` by contract, so the
                  // index IS the identity here.
                  data.map((_, di) => (
                    <Cell key={di} fill={c.paint(perCategory[di])} />
                  ))
                : null}
            </Bar>
          ))}
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
}
